import React, { useEffect, useState } from "react";

const PRELOADER_STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300&family=DM+Sans:wght@300;400;500&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --navy: #080d1a;
  --gold: #c9a84c;
  --goldl: #e2c47a;
  --muted: #6b7280;
  --dim: #374151;
  --text: #f0ede8;
}

html, body, #preloader-root {
  height: 100%; width: 100%;
  background: var(--navy);
  font-family: 'DM Sans', sans-serif;
}

.pl-wrap {
  position: fixed; inset: 0;
  background: var(--navy);
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  z-index: 99999;
  transition: opacity 0.9s ease, visibility 0.9s ease;
}
.pl-wrap.done { opacity: 0; visibility: hidden; pointer-events: none; }

/* glow orb */
.pl-glow {
  position: absolute;
  width: 480px; height: 480px; border-radius: 50%;
  background: radial-gradient(circle, rgba(201,168,76,.07) 0%, transparent 70%);
  animation: glowPulse 3s ease-in-out infinite;
}
@keyframes glowPulse {
  0%,100% { transform: scale(1); opacity: .5; }
  50% { transform: scale(1.2); opacity: 1; }
}

/* rings */
.pl-rings {
  position: relative; width: 100px; height: 100px;
  margin-bottom: 36px;
}
.pl-ring {
  position: absolute; inset: 0; border-radius: 50%;
  border: 1px solid rgba(201,168,76,.2);
  animation: ringExpand 2.6s ease-out infinite;
}
.pl-ring:nth-child(2) { animation-delay: .55s; inset: -16px; }
.pl-ring:nth-child(3) { animation-delay: 1.1s;  inset: -32px; }
@keyframes ringExpand {
  0%   { opacity: 1; transform: scale(.65); }
  100% { opacity: 0; transform: scale(1.35); }
}

/* emblem */
.pl-emblem {
  position: absolute; inset: 0; border-radius: 28px;
  background: linear-gradient(135deg, rgba(201,168,76,.12), rgba(201,168,76,.04));
  border: 1px solid rgba(201,168,76,.28);
  display: flex; align-items: center; justify-content: center;
}
.pl-emblem-letter {
  font-family: 'Cormorant Garamond', serif;
  font-size: 38px; font-weight: 300;
  color: var(--gold); line-height: 1;
}

/* text */
.pl-logo {
  font-family: 'Cormorant Garamond', serif;
  font-size: 34px; font-weight: 300; letter-spacing: 6px;
  color: var(--text);
  opacity: 0; animation: fadeUp .7s ease .35s forwards;
}
.pl-logo span { color: var(--gold); }

.pl-tagline {
  font-size: 10px; letter-spacing: 5px; text-transform: uppercase;
  color: var(--muted); margin-top: 6px; margin-bottom: 52px;
  opacity: 0; animation: fadeUp .7s ease .65s forwards;
}

/* bar */
.pl-bar-wrap {
  width: 180px; height: 1px;
  background: rgba(255,255,255,.05); border-radius: 1px; overflow: hidden;
  opacity: 0; animation: fadeUp .5s ease .9s forwards;
}
.pl-bar {
  height: 100%;
  background: linear-gradient(90deg, transparent, var(--gold), var(--goldl), transparent);
  animation: barSweep 1.8s ease 1.05s infinite;
}
@keyframes barSweep {
  0%   { width: 0;   margin-left: 0; }
  50%  { width: 100%; margin-left: 0; }
  100% { width: 15%;  margin-left: 100%; }
}

.pl-status {
  font-size: 10px; color: var(--dim);
  margin-top: 14px; letter-spacing: 3px; text-transform: uppercase;
  opacity: 0; animation: fadeUp .5s ease .9s forwards;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

interface PreloaderProps {
  duration?: number; // ms, default 2800
  onDone?: () => void;
}

export default function Preloader({ duration = 2800, onDone }: PreloaderProps) {
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Inject styles once
    if (!document.getElementById('pl-styles')) {
      const s = document.createElement('style');
      s.id = 'pl-styles';
      s.textContent = PRELOADER_STYLES;
      document.head.appendChild(s);
    }
    const t = setTimeout(() => {
      setDone(true);
      onDone?.();
    }, duration);
    return () => clearTimeout(t);
  }, [duration, onDone]);

  return (
    <div className={`pl-wrap${done ? ' done' : ''}`}>
      <div className="pl-glow" />

      <div className="pl-rings">
        <div className="pl-ring" />
        <div className="pl-ring" />
        <div className="pl-ring" />
        <div className="pl-emblem">
          <span className="pl-emblem-letter">N</span>
        </div>
      </div>

      <div className="pl-logo">NATIONSTAR<span> Bank</span></div>
      <div className="pl-tagline">Private Banking · Est. 1998</div>

      <div className="pl-bar-wrap">
        <div className="pl-bar" />
      </div>
      <div className="pl-status">Securing your session…</div>
    </div>
  );
}
